import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdTrendingUp,
  MdAssessment,
  MdAnalytics,
  MdShowChart
} from 'react-icons/md';

const Report = () => {
  const navigate = useNavigate();
  const [selectedReport, setSelectedReport] = useState(null);

  const reports = [
    {
      id: 'daily-flow',
      title: 'Daily Report Flow',
      description: 'Real-time monitoring of daily operations, production metrics, and workflow efficiency.',
      icon: MdAssessment,
      color: 'green',
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        icon: 'text-blue-600',
        hover: 'hover:bg-blue-100',
        gradient: 'from-blue-500 to-blue-600',
        status: 'bg-blue-100 text-blue-700'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        icon: 'text-green-600',
        hover: 'hover:bg-green-100',
        gradient: 'from-green-500 to-green-600',
        status: 'bg-green-100 text-green-700'
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  const handleReportClick = (report) => {
    if (report.id === 'daily-flow') {
      navigate('/admin/finished-products/reports/daily');
    } else if (report.id === 'sales-forecast') {
      navigate('/admin/finished-products/reports/sales-forecast');
    } else {
      setSelectedReport(report);
      // Here you can add navigation logic to specific report pages
      console.log('Selected report:', report.id);
    }
  };

  return (
    <div className="mt-5 p-4">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transform hover:scale-105 transition-all duration-200"
          >
            <div className="bg-blue-50 p-2 rounded-lg transform rotate-3 shadow-md">
              <MdArrowBack size={24} />
            </div>
            <span className="font-medium">Back to Dashboard</span>
          </button>
        </div>
      </div>

      {/* Page Header */}
      <div className="mb-8">
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg">
          <div className="flex items-center space-x-4">
            <div className="bg-gradient-to-br from-purple-500 to-blue-600 p-4 rounded-lg transform rotate-3 shadow-lg">
              <MdAnalytics className="text-white" size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Reports & Analytics</h1>
              <p className="text-gray-600 mt-1">Access comprehensive reports and insights for informed decision-making</p>
            </div>
          </div>
        </div>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        {reports.map((report) => {
          const colors = getColorClasses(report.color);
          const IconComponent = report.icon;
          
          return (
            <div
              key={report.id}
              onClick={() => handleReportClick(report)}
              className={`group cursor-pointer transform hover:-translate-y-2 transition-all duration-300 rounded-2xl p-8 hover:shadow-2xl ${colors.bg} border-2 ${colors.border}`}
            >
              <div className="flex flex-col items-center text-center">
                <div className={`p-5 rounded-full bg-white shadow-lg mb-5`}>
                  <IconComponent className={colors.icon} size={48} />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">
                  {report.title}
                </h3>
                <p className={`mt-4 text-sm font-medium ${colors.icon}`}>View Report &rarr;</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected Report Modal */}
      {selectedReport && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setSelectedReport(null)}
            />

            {/* Modal */}
            <div className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
              {/* Close button */}
              <button
                onClick={() => setSelectedReport(null)}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Modal content */}
              <div className="flex items-center space-x-4 mb-6">
                <div className={`p-3 rounded-lg ${getColorClasses(selectedReport.color).bg}`}>
                  <selectedReport.icon className={getColorClasses(selectedReport.color).icon} size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">{selectedReport.title}</h3>
                  <p className="text-gray-600">{selectedReport.description}</p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  This report provides detailed insights and analytics to help you make informed decisions. 
                  Click the button below to access the full report with interactive charts and detailed data.
                </p>
                
                <div className="flex space-x-3">
                  <button 
                    onClick={() => handleReportClick(selectedReport)}
                    className="flex-1 py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors">
                    Open Full Report
                  </button>
                  <button className="py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors">
                    Download PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Report; 